/** One score in the items table, coloured by pass/fail against the evaluator's threshold. */

import { formatScore } from "@/lib/format";
import type { ItemRow, ItemScore } from "../types";

export function ScoreCell({ score, row }: { score: ItemScore | undefined; row: ItemRow }) {
  if (!score) {
    const text = row.status === "agent_error" || row.status === "not_run" ? "—" : "·";
    return <span className="score score-wait">{text}</span>;
  }
  if (score.status === "not_applicable")
    return (
      <span className="score score-wait" title="Not applicable to this item">
        n/a
      </span>
    );
  if (score.status === "error")
    return (
      <span className="score score-err" title="The evaluator failed on this item">
        err
      </span>
    );
  const label = score.passed === false ? "failed" : score.passed ? "passed" : undefined;
  return (
    <span className={`score ${score.passed === false ? "score-fail" : ""}`} title={label}>
      {formatScore(score.value)}
    </span>
  );
}

const WAITING: Record<string, string> = { pending: "Queued", running: "Scoring…", not_run: "Not run" };

/** The output column: the answer, or why there is none. */
export function OutputCell({ row }: { row: ItemRow }) {
  if (row.status === "agent_error")
    return <span className="clamp-2 text-err">Agent error{row.error_code ? `: ${row.error_code}` : ""}</span>;
  const waiting = WAITING[row.status];
  if (waiting) return <span className="text-muted">{waiting}</span>;
  return (
    <span className="flex flex-col gap-0.5">
      <span className="clamp-2">{row.output_preview}</span>
      {row.version_drift && (
        <span
          className="text-xs text-warn"
          title="The platform reported another version than the pinned one."
        >
          Drift: answered by v{row.reported_version ?? "?"}
        </span>
      )}
    </span>
  );
}
